/**
 * Every backend endpoint this application calls, named once.
 *
 * <p>Kept apart from {@link ROUTES}: those are pages in the browser, these are
 * resources on the server, and the two moving independently is normal. A single
 * map of "paths" would hide which kind each one is.
 */
export const API = {
  /** OAuth 2.1 token endpoint. Not under /api — it is the authorization server. */
  token: '/oauth2/token',

  /**
   * Where to send the browser to begin signing in with a provider.
   *
   * <p>A function because the provider is part of the path. Answers the
   * provider's consent URL with this application's client id, scopes, redirect
   * URI and a freshly issued `state` already in it — so nothing here builds that
   * URL, and the `state` exists on the server before the browser ever leaves.
   *
   * <p>Public: nobody is signed in when a sign-in begins. Each call issues a new
   * `state`, so two tabs do not interfere and an abandoned attempt expires on its
   * own.
   */
  socialSignIn: (provider: string) => `/api/v1/social-sign-in/${provider}`,

  signUp: '/api/v1/sign-up',

  /**
   * Analyses a website and answers its brand — logo, colours, fonts, images, company. POST.
   *
   * <p>Signed in, and **the user is the token's**: the body carries the site and which social
   * profiles to read, never an identifier for the caller. Slow — it can take minutes when the
   * brand is not already stored — so the caller passes its own timeout.
   */
  forward: '/api/v1/forward',

  /**
   * The company's API keys. GET lists them (a cursor page), POST creates one and answers the secret
   * ONCE. Everything below this is one key, by its public identifier.
   */
  apiKeys: '/api/v1/api-keys',
  apiKey: (keyId: string | number) => `/api/v1/api-keys/${String(keyId)}`,
  apiKeyRegenerate: (keyId: string | number) => `/api/v1/api-keys/${String(keyId)}/regenerate`,
  apiKeyRevoke: (keyId: string | number) => `/api/v1/api-keys/${String(keyId)}/revoke`,
  apiKeyDomain: (keyId: string | number) => `/api/v1/api-keys/${String(keyId)}/domain`,
  /** One calendar month per row: the calls a key made and the quota it was held to. */
  apiKeyMonthlyUsage: (keyId: string | number) => `/api/v1/api-keys/${String(keyId)}/monthly-usage`,
  /** A key's request counts over `days` (1 to 90). */
  apiKeyStatistics: (keyId: string | number) => `/api/v1/api-keys/${String(keyId)}/statistics`,
  apiKeyRequestLogs: (keyId: string | number) => `/api/v1/api-keys/${String(keyId)}/request-logs`,

  /** Asks for developer access (POST) and reads the caller's request (GET). A person asks once. */
  developerAccess: '/api/v1/developer-access',
  developerAccessRequest: '/api/v1/developer-access/request',

  /**
   * The plan catalogue. GET lists it, newest pricing-page order; `moduleKey` narrows it to one module's plans
   * and `includeInactive` adds those off sale. Signed in, any role.
   */
  planConfigurations: '/api/v1/plan-configurations',

  /** The company's plan, expiry and billing cycle. GET. Primary admins and admins only; 404 when it has none. */
  billingPlan: '/api/v1/billing/plan',

  /**
   * The company's plan requests. GET lists them (newest first); POST asks for one. Primary admins and
   * admins only. One may be open at a time: a second is a 409.
   */
  customPlanRequests: '/api/v1/custom-plan-requests',

  /**
   * Buying a module plan, and managing it afterwards. Primary admins and admins only, and every one is on
   * the company the signed-in person acts for: no path or body names a company or a person.
   *
   * <p>`moduleSubscriptions` lists what the company holds. `moduleCheckout` (POST) starts a Stripe Checkout
   * and answers its address; `moduleCheckoutConfirm` (POST) is called when the customer returns from it.
   * The two after are a PUT and a POST on one module's subscription. (The backend also has `POST …/{moduleKey}/cancel`, which the pricing page does not use: turning auto pay off is the same cancellation at the period's end.) `stripeConfig` says whether online
   * payment is set up on the server at all.
   */
  stripeConfig: '/api/v1/billing/stripe-config',
  moduleSubscriptions: '/api/v1/billing/modules',
  moduleCheckout: '/api/v1/billing/modules/checkout-sessions',
  moduleCheckoutConfirm: (sessionId: string) =>
    `/api/v1/billing/modules/checkout-sessions/${encodeURIComponent(sessionId)}/confirm`,
  moduleAutoPay: (moduleKey: string) =>
    `/api/v1/billing/modules/${encodeURIComponent(moduleKey)}/auto-pay`,
  moduleChangePlan: (moduleKey: string) =>
    `/api/v1/billing/modules/${encodeURIComponent(moduleKey)}/change-plan`,
  /**
   * Pays to upgrade a paid plan: a Stripe Checkout for the difference, and the plan changes once it is paid. POST,
   * answered with the `checkoutUrl` to send the browser to.
   */
  moduleChangePlanCheckout: (moduleKey: string) =>
    `/api/v1/billing/modules/${encodeURIComponent(moduleKey)}/change-plan-checkout`,

  /** The signed-in person's own profile. GET reads it; PUT replaces the fields they may change. */
  accountProfile: '/api/v1/account/profile',
  /** The signed-in person's profile picture. PUT, multipart, one part named `file`; answers the profile. */
  accountProfileImage: '/api/v1/account/profile/image',
  /** The signed-in person's cover picture. PUT, multipart, one part named `file`; answers the profile. */
  accountCoverImage: '/api/v1/account/profile/cover-image',
  /** The people in the signed-in person's own company: name, role and picture. GET. */
  accountColleagues: '/api/v1/account/colleagues',
  /** Changes the signed-in person's password. POST; every session ends, so the person signs in again. */
  accountPassword: '/api/v1/account/password',
  /** Stops a paid module subscription renewing, or ends a free one. POST. */
  moduleCancel: (moduleKey: string) =>
    `/api/v1/billing/modules/${encodeURIComponent(moduleKey)}/cancel`,

  /** The usage cards at the top of the API console: calls, domains and what is left of the allowance. GET. */
  dashboardUserCards: '/api/v1/dashboard/user/cards',

  /** This month's brand analyses for the signed-in person, against their allowance. GET. */
  analysisQuota: '/api/v1/analysis-quota',

  /**
   * The platform operator's billing. Every one is `@SuperAdminOnly`: the plan catalogue, the custom
   * plan requests of every company, and the two audit lists. A plan is changed with PUT (a full
   * replacement) and deleted with DELETE, which a plan in use refuses with 409.
   */
  planConfiguration: (planId: string) =>
    `/api/v1/plan-configurations/${encodeURIComponent(planId)}`,
  /** What one plan grants: its features, each with the limit in force. GET. Any signed-in person. */
  planConfigurationFeatures: (planId: string) =>
    `/api/v1/plan-configurations/${encodeURIComponent(planId)}/features`,
  adminCustomPlanRequests: '/api/v1/admin/custom-plan-requests',
  adminCustomPlanRequestApprove: (requestId: string) =>
    `/api/v1/admin/custom-plan-requests/${encodeURIComponent(requestId)}/approve`,
  adminCustomPlanRequestReject: (requestId: string) =>
    `/api/v1/admin/custom-plan-requests/${encodeURIComponent(requestId)}/reject`,
  /**
   * Makes a Stripe payment link again for a negotiated plan that is waiting to be paid. POST. Super admin
   * only; the first link could not be made, or expired.
   */
  adminModuleSubscriptionPaymentLink: (subscriptionId: string) =>
    `/api/v1/admin/billing/module-subscriptions/${encodeURIComponent(subscriptionId)}/payment-link`,
  adminQuotaResets: '/api/v1/admin/billing/quota-resets',
  adminAiSpendAlerts: '/api/v1/admin/billing/ai-spend-alerts',

  /** The product catalogue, a page at a time. The plan form lists it to say which product a plan sells. */
  productCatalogue: '/api/v1/products',

  /**
   * Asks the extraction service about a stored brand's website again and updates the brand. POST.
   *
   * <p>A function because the brand is part of the path. Any signed-in user may call it, and it is
   * as slow as an analysis, so the caller passes its own timeout.
   */
  brandReanalyze: (brandId: string | number) => `/api/v1/brands/${String(brandId)}/reanalyze`,

  /** The caller's plan usage and totals over everything they have analysed. GET. */
  brandDashboardSummary: '/api/v1/brands/dashboard/summary',

  /**
   * A page of the caller's analysed domains with the count of each status. GET, paged by
   * `page` and `size`, filtered by `status` and `search`.
   */
  brandDashboardBrands: '/api/v1/brands/dashboard/brands',
  /** The brand that carries a website address. GET `?website=`; 404 when there is none. */
  brandByWebsite: '/api/v1/brands/by-website',
  /** A brand's review figures and the reviews of the last seven days. GET. */
  brandReviewStats: (brandId: string | number) => `/api/v1/reviews/brands/${String(brandId)}/stats`,
  /** The latest pros and cons analysis of a brand's reviews. GET; 404 until one has run. */
  brandProsCons: (brandId: string | number) =>
    `/api/v1/brands/${String(brandId)}/reviews/pros-cons`,
  /** Runs a new pros and cons analysis of a brand's reviews, which can take minutes. POST. */
  brandAnalyseProsCons: (brandId: string | number) =>
    `/api/v1/brands/${String(brandId)}/reviews/analyze/pros-cons`,
  /**
   * The domains this product refuses, so a form can say no without a round trip.
   *
   * Public — sign-up has no session — and the same list guards sign-up, adding a team
   * member and connecting a warmup mailbox. The server still refuses the request itself.
   */
  blockedEmailDomains: '/api/v1/blocked-email-domains',

  /**
   * Whether one registration has been activated yet.
   *
   * A function rather than a string, because the id is part of the path. Built
   * here so no screen concatenates its own and the shape stays in one file.
   */
  signUpStatus: (id: string) => `/api/v1/sign-up/${id}/status`,
  activate: '/api/v1/activate',
  /**
   * Asks whether an activation link can still be spent, **without spending it**.
   *
   * <p>A POST that changes nothing: a token in a query string reaches every proxy log, the
   * browser's history and the `Referer` of whatever loads next, which is why `activate` is a
   * POST too. Unlike `activate` it is safe for a mail scanner to follow.
   */
  activationStatus: '/api/v1/activate/check',
  resendActivation: '/api/v1/activate/resend',

  /**
   * Asks for a password reset link. Answers 202 with an empty body for every
   * address, so nothing here can be used to discover who holds an account.
   */
  passwordReset: '/api/v1/password-reset',

  /** Spends a reset token and sets the new password. */
  passwordResetConfirm: '/api/v1/password-reset/confirm',

  /**
   * Asks for a sign-in link. Answers 200 with one fixed sentence for every
   * address — one that was sent a link, one whose account may not sign in, and
   * one nobody has registered — so nothing here discovers who holds an account.
   *
   * <p>There is no endpoint beside this one for spending the link: that is a
   * grant on {@link API.token}, not a request of its own.
   */
  magicLink: '/api/v1/magic-link',

  logout: '/api/v1/auth/logout',
  currentUser: '/api/v1/account/me',

  /**
   * The people in the company, paged.
   *
   * Takes no company identifier: the tenant is whichever one the session is
   * acting in. Search, sort and paging are query parameters — see
   * `features/team/api/teamMembers.ts`.
   */
  team: '/api/v1/team',

  /**
   * The three counts above the team table, and the caller's own row.
   *
   * Its own endpoint, not a field on the list: the tiles and the rows change for
   * different reasons and on different timescales, and carried together the
   * counts were recomputed on every page click and every keystroke.
   */
  teamSummary: '/api/v1/team/summary',

  /**
   * One person, with every product they hold.
   *
   * <p>A function because the identifier is in the path. The list row's `id` is what goes
   * here — a public UUID, never an internal one.
   */
  teamMember: (externalId: string) => `/api/v1/team/${externalId}`,

  /**
   * Stop somebody signing in, and end every session they hold.
   *
   * <p>A POST on a sub-path rather than a PATCH on the person: the representation has no
   * `enabled` field — it publishes `status`, which is derived — and the main effect is not a
   * field change at all but a delete in another table. A named path also says which of the
   * two happened in the access log, which a request body cannot.
   *
   * <p>Takes no body and answers the list row, so the row that was clicked can be replaced
   * in place.
   */
  teamMemberSuspend: (externalId: string) => `/api/v1/team/${externalId}/suspend`,

  /**
   * Let them sign in again, with the role, privileges and products they already had.
   *
   * <p>Its own path rather than the one above with a flag, because they are not the same
   * operation: suspending revokes every session, and this revokes nothing — there is
   * nothing left to revoke.
   */
  teamMemberReactivate: (externalId: string) => `/api/v1/team/${externalId}/reactivate`,

  /**
   * Send an invited person their activation link again.
   *
   * <p>For somebody who never received the first email, or whose link expired. They cannot
   * ask for one themselves in any useful way: `POST /api/v1/activate/resend` exists for
   * somebody who signed themselves up and is standing at the screen that told them to check
   * their inbox, and it answers everybody the same empty 202 — so an invitee posting there
   * gets no mail and no explanation. The administrator pushes it from their row instead.
   *
   * <p>Takes no body and answers **202 with nothing**. Issuing the new link deletes the
   * previous unspent one, so the older email stops working; nothing about the person
   * changes, which is why there is no row to send back.
   *
   * <p>**Only for a PENDING row.** Somebody suspended or already activated is refused with
   * 403 and its own sentence.
   */
  teamMemberResendInvitation: (externalId: string) =>
    `/api/v1/team/${externalId}/resend-invitation`,

  /**
   * What the acting company may hand out: its products, and the privileges inside each.
   *
   * <p>The vocabulary an assignment form needs, and the only honest source for it. The picker was
   * built from `NAV_ITEMS` before this, which got three things wrong at once — it showed
   * "Xtremand AI Agent" twice because one menu entry fronts two products, it called Brand
   * API "Developer Tools" because that is the menu's label, and it had no product ids and
   * no privileges, which is what `POST /api/v1/team` actually takes.
   *
   * <p>It is the company's <b>ceiling</b>, so a tick it offers cannot be refused when the
   * privilege is assigned.
   */
  productPrivileges: '/api/v1/product-privileges',

  /**
   * The greeting behind each product, keyed by the product key the API answers
   * with.
   *
   * <p>They are placeholders while the products themselves are unbuilt; as each real
   * screen arrives it calls its own endpoints and its entry here goes.
   *
   * <p><b>Nothing refuses one.</b> This said every entry sat behind the server's
   * product gate and that a company without the subscription got 403 and
   * `product-not-subscribed` — true until 24 September 2026, when plans, subscriptions
   * and the padlock drawn from them were cut, that problem type with them. These
   * endpoints are open to anybody signed in, and a comment promising a gate is worse
   * than none: it reads as a reason not to check.
   *
   * <p>Keyed rather than listed, because the key is what everything else already
   * joins on — the menu, and the entries below that have been wrong about theirs.
   */
  productWelcome: {
    email_warmup: '/api/v1/email-warmup/welcome',
    reviews_rankings: '/api/v1/reviews-rankings/welcome',

    /*
     * `brand_api`, at `/api/v1/brand-api/welcome`. Both halves of this line were wrong
     * until 30 September 2026: the KEY was `api_access`, removed from the catalogue on
     * 17 September, so nothing ever joined against it; and the PATH was
     * `/api/v1/developer-tools/welcome`, which is where the placeholder controller sat
     * before its module was renamed to the product it belongs to.
     */
    brand_api: '/api/v1/brand-api/welcome',

    /*
     * Added 30 September 2026 with the module behind it. The catalogue has sold this
     * since 26 September and nothing in either repository named it -- no module, no
     * endpoint, no entry here -- so a company holding it had a product that answered
     * nothing at all.
     */
    lead_meeting_intelligence: '/api/v1/lead-meeting-intelligence/welcome',

    linkedin_ai_agent: '/api/v1/linkedin-agent/welcome',
    seo_ai_agent: '/api/v1/seo-agent/welcome',

    brand_intelligence: '/api/v1/brand-intelligence/welcome',

    /*
     * This said "no entry for lead_meeting_intelligence or brand_api" until 3 October 2026,
     * three lines below both of them. It was true when written on 26 September, when neither
     * had a module or a controller; both got one on 30 September and were added above
     * without this paragraph being read.
     *
     * The shape is still the point and is worth keeping: the map is KEYED, not listed, so a
     * product nobody has built is a missing key the lookup answers `undefined` for, rather
     * than a wrong path that 404s at runtime. Every product on sale has an entry today.
     */
  } as Record<string, string | undefined>,

  /**
   * Every mailbox the **acting company** has connected for warming, newest first.
   *
   * <p>Takes no company identifier, like `team` above: the tenant is whichever one the
   * session is acting in, resolved server-side by `caller.requireActingCompanyId()`. The
   * previous system's `/api/warmup/my-accounts` was scoped to the signed-in *person*
   * instead, and that difference is deliberate — a mailbox is a company asset that
   * colleagues operate, and who connected it is recorded on the row and not published.
   *
   * <p>**Not paged, not filtered, not sortable.** No `page`, no `q`, no `provider`, no
   * `status` — backend `email-warmup.md` EW-91 to EW-95. A company holds as many mailboxes
   * as it pays for and the screen reads them whole; searching and filtering are the
   * browser's job over the loaded array.
   */
  emailWarmupMailAccounts: '/api/v1/email-warmup/mail-accounts',

  /**
   * Tries SMTP and, optionally, IMAP settings. **Saves nothing and sends no mail.**
   *
   * <p>A sibling of the collection rather than a member, because it acts on settings that
   * have no mailbox yet — there is no `{externalId}` to address.
   */
  emailWarmupTestConnection: '/api/v1/email-warmup/mail-accounts/test-connection',

  /**
   * One mailbox, by its public identifier.
   *
   * <p>`externalId` is the **UUID**, never the internal `BIGINT` — which this API neither
   * accepts in a path nor publishes in any response (EW-90, EW-104).
   *
   * <p>The lookup matches `external_id` **and** `company_id` together, so an identifier
   * belonging to another company matches no row and answers the same 404 as an unknown one
   * (EW-99, EW-100).
   */
  emailWarmupMailAccount: (externalId: string) =>
    `/api/v1/email-warmup/mail-accounts/${externalId}`,

  /**
   * Where a mailbox stands, its lifetime totals, and a window of its daily history.
   *
   * <p>Takes `days` — **1 to 365, default 30** (EW-113). Outside that range is a 400
   * `validation-failed` naming `days`, so the caller clamps rather than sending whatever a
   * control produced.
   *
   * <p>**`totals` is lifetime and `days` is the window.** The totals cover the whole
   * recorded history whatever `days` is set to (EW-108) — a lifetime figure that moved with
   * a query parameter would not be a lifetime figure.
   */
  emailWarmupMailAccountStatus: (externalId: string) =>
    `/api/v1/email-warmup/mail-accounts/${externalId}/status`,

  /**
   * The two headline figures: lifetime sends, and the mean daily spam score.
   *
   * <p>It carries neither the account nor its history — those are the two endpoints above.
   * The previous system's equivalent serialised the whole entity and the whole status list,
   * which published the internal id and the extension columns holding the OAuth tokens
   * (EW-118).
   */
  emailWarmupMailAccountStats: (externalId: string) =>
    `/api/v1/email-warmup/mail-accounts/${externalId}/stats`,

  /**
   * What the last DNS pass found about the mailbox's domain: the records as published, what
   * each is worth, what to fix, the blocklists and the registry.
   *
   * <p>**Nothing is looked up when it is read** — it returns what the last pass stored, and a
   * mailbox that has not been through one answers 200 with `snapshotMissing` true.
   */
  emailWarmupMailAccountDnsDetails: (externalId: string) =>
    `/api/v1/email-warmup/mail-accounts/${externalId}/dns-details`,

  /**
   * What the last DKIM read-back stored: the selector, the signing domain, what the receiving server
   * reported and when. GET; nothing is looked up when it is read.
   */
  emailWarmupMailAccountDkimLastValidation: (externalId: string) =>
    `/api/v1/email-warmup/mail-accounts/${externalId}/dkim-last-validation`,

  /**
   * Runs the DNS and DKIM checks again. **POST, and it answers at once**: the mailbox goes
   * back to `PENDING_VERIFICATION` and the checks run in the background, so the caller reads
   * the mailbox or its DNS details again later.
   */
  emailWarmupMailAccountReverify: (externalId: string) =>
    `/api/v1/email-warmup/mail-accounts/${externalId}/reverify`,

  /**
   * The mailbox's SMTP and IMAP settings, for the edit form. GET reads them (no password,
   * ever); PUT changes the ones it names after both servers accept the result.
   */
  emailWarmupMailAccountConnectionSettings: (externalId: string) =>
    `/api/v1/email-warmup/mail-accounts/${externalId}/custom-smtp-config`,

  /** Gives the mailbox new SMTP and IMAP passwords. POST; neither is returned. */
  emailWarmupMailAccountReauthenticate: (externalId: string) =>
    `/api/v1/email-warmup/mail-accounts/${externalId}/reauth-custom`,

  /**
   * How a mailbox is configured for warming: its tier, the tiers its plan allows, its start
   * date and its communication profile. A read; it checks nothing and changes nothing.
   */
  emailWarmupMailAccountConfiguration: (externalId: string) =>
    `/api/v1/email-warmup/mail-accounts/${externalId}/configuration`,

  /**
   * Configures a mailbox: the tier, the start date and, optionally, the profile. POST.
   *
   * <p>Puts the mailbox on day 0 as `SCHEDULED` and sends the person a confirmation email.
   * **The plan is not checked yet** — any configured tier is accepted.
   */
  emailWarmupMailAccountConfigure: (externalId: string) =>
    `/api/v1/email-warmup/mail-accounts/${externalId}/configure`,

  /** The kinds of warmup email a mailbox can send. Global: every tenant sees the same list. */
  emailWarmupTemplateCategories: '/api/v1/email-warmup/template-categories',

  /**
   * The categories, for the operator's screens. The customer read above needs the Email Warmup
   * privilege, which the operator does not hold.
   */
  emailWarmupTemplateCategoryChoices: '/api/v1/email-warmup/templates/categories',

  /** One template category, to change it. The operator's. */
  emailWarmupTemplateCategory: (externalId: string) =>
    `/api/v1/email-warmup/template-categories/${externalId}`,

  /** The warmup templates: GET lists them and POST saves one. The operator's. */
  emailWarmupTemplates: '/api/v1/email-warmup/templates',

  /** One template, to change it. */
  emailWarmupTemplate: (externalId: string) => `/api/v1/email-warmup/templates/${externalId}`,

  /**
   * Where an Outlook receiver signs in at Microsoft. Opened in a new tab, not called: it answers with a
   * redirect, and the person's browser follows it.
   */
  emailWarmupMicrosoftLogin: '/api/v1/email-warmup/microsoft/login',

  /**
   * Starts signing a Google or Microsoft mailbox in. GET, answers the provider's sign-in address, which
   * the browser opens in a window; `mailAccountId` makes it a sign-in again of that mailbox.
   */
  emailWarmupOAuthAuthorize: (provider: 'google' | 'microsoft') =>
    `/api/v1/email-warmup/oauth/${provider}/authorize`,

  /** The AI providers a template can be written with in this environment. */
  emailWarmupTemplateProviders: '/api/v1/email-warmup/templates/providers',

  /** Has the AI write a template for somebody to read. POST; nothing is saved. */
  emailWarmupTemplateGenerate: '/api/v1/email-warmup/templates/generate',

  /** The AI provider keys and models of this environment. The operator's. */
  emailWarmupAiProviders: '/api/v1/email-warmup/ai-providers',

  /** One key-and-model row, to change it. PATCH. */
  emailWarmupAiProvider: (externalId: string) => `/api/v1/email-warmup/ai-providers/${externalId}`,

  /**
   * Pauses a mailbox's warmup. POST, with an optional reason.
   *
   * <p>Accepted for a mailbox in any state. The person is emailed a confirmation.
   */
  emailWarmupMailAccountPause: (externalId: string) =>
    `/api/v1/email-warmup/mail-accounts/${externalId}/pause`,

  /**
   * Resumes a mailbox's warmup. POST, no body.
   *
   * <p>409 `warmup-already-completed` once the warmup has finished.
   */
  emailWarmupMailAccountResume: (externalId: string) =>
    `/api/v1/email-warmup/mail-accounts/${externalId}/resume`,

  /**
   * Starts another sending round for today at once. POST, no body.
   *
   * <p>404 `warmup-day-not-found` when the daily job has not planned today for the mailbox yet.
   */
  emailWarmupMailAccountContinue: (externalId: string) =>
    `/api/v1/email-warmup/mail-accounts/${externalId}/trigger-continuation`,

  /**
   * The warmup receiver pool, for the platform operator. GET pages it by cursor; POST adds one
   * Gmail receiver. Every verb is `@SuperAdminOnly`.
   */
  emailWarmupReceivers: '/api/v1/email-warmup/receivers',

  /**
   * What the custom contacts sender did each day for a mailbox, newest first. GET. An empty list
   * is a normal answer: the sender writes the rows and has not run until it has sent.
   */
  emailWarmupCustomContactDays: (externalId: string) =>
    `/api/v1/email-warmup/mail-accounts/${externalId}/custom-contacts/status/daily`,

  /** Adds up to 500 Gmail receivers at once. POST. */
  emailWarmupReceiversBulk: '/api/v1/email-warmup/receivers/bulk',

  /** How many Gmail receivers a deployment's pool holds. GET. */
  emailWarmupReceiversStats: '/api/v1/email-warmup/receivers/stats',

  /**
   * A mailbox's custom contact list. GET reads it; a mailbox with no list is a 200 with
   * `uploaded: false`, not a 404. The list is capped by tier, so it is not paged.
   */
  emailWarmupCustomContacts: (externalId: string) =>
    `/api/v1/email-warmup/mail-accounts/${externalId}/custom-contacts`,

  /** Replaces the list from a CSV file. POST, multipart. Only `.csv` is read. */
  emailWarmupCustomContactsUpload: (externalId: string) =>
    `/api/v1/email-warmup/mail-accounts/${externalId}/custom-contacts/upload`,

  /** Adds one contact. POST; 201 with the contact. */
  emailWarmupCustomContactsAdd: (externalId: string) =>
    `/api/v1/email-warmup/mail-accounts/${externalId}/custom-contacts/contacts`,

  /**
   * One contact, by its own identifier and never by its address, which would travel in the
   * URL and land in every access log. PUT changes it; DELETE removes it.
   */
  emailWarmupCustomContact: (externalId: string, contactId: string) =>
    `/api/v1/email-warmup/mail-accounts/${externalId}/custom-contacts/contacts/${contactId}`,
  /**
   * Analyses a website and stores the brand. POST `{ url }`. **Not the SEO agent's endpoint**
   * -- it belongs to Brand Analyser -- but the SEO setup cannot start without it: offerings,
   * competitors and the plans are all generated from the brand it creates.
   */
  brandForward: '/api/v1/forward',

  // ── SEO agent ──────────────────────────────────────────────────────────────────────────
  // Every path answers for the caller's own site: the user, company and site come from the
  // session, so none of these takes a user, client or site id. `docs/features/seo-agent-apis.md`
  // in the backend lists what each one does. Endpoints ending in `Live` stream
  // `text/event-stream`.

  /** POST generates the sell / don't-sell lists for `{ url }`; PUT saves an edited pair. */
  seoAgentOfferings: '/api/v1/seo-agent/offerings',

  /** POST generates competitors with authority scores; PUT saves an edited list. */
  seoAgentCompetitors: '/api/v1/seo-agent/competitors',

  /** The caller's latest setup answers. GET (404 before setup), POST saves, PUT updates. */
  seoAgentConfig: '/api/v1/seo-agent/agent-config',

  /** Generates the agent's prompt from the saved setup and stores it on the configuration. */
  seoAgentPrompt: '/api/v1/seo-agent/agent-prompt',

  /** POST generates the content calendar for `{ url }`. */
  seoAgentContentPlan: '/api/v1/seo-agent/content-plan',

  /** POST generates the SEO plan for `{ url }`; GET `?url=` reads the current one. */
  seoAgentExecutionPlan: '/api/v1/seo-agent/execution-plan',

  /** The caller's site connection. POST saves, GET reads, PUT updates, DELETE removes. */
  seoAgentSite: '/api/v1/seo-agent/site',
  seoAgentSiteValidateCredentials: '/api/v1/seo-agent/site/validate-credentials',
  seoAgentSiteValidateGoogle: '/api/v1/seo-agent/site/validate-google-credentials',
  seoAgentWordPressValidate: '/api/v1/seo-agent/wordpress/validate',
  seoAgentSiteExecute: '/api/v1/seo-agent/site/execute-seo-plan',
  seoAgentSiteExecuteLive: '/api/v1/seo-agent/site/execute-seo-plan/live',
  seoAgentContentPlanRunning: '/api/v1/seo-agent/site/content-plan/running',
  seoAgentContentPlanRetry: (scheduledDate: string) =>
    `/api/v1/seo-agent/site/content-plan/retry/${scheduledDate}`,
  seoAgentContentPlanRetryLive: (scheduledDate: string) =>
    `/api/v1/seo-agent/site/content-plan/retry/${scheduledDate}/live`,

  /** The AI team. `teamId` and `executionId` are public identifiers (UUIDs). */
  seoAgentOrgCreate: '/api/v1/seo-agent/org-agent/create',
  seoAgentOrgMe: '/api/v1/seo-agent/org-agent/me',
  seoAgentOrgTeam: (teamId: string) => `/api/v1/seo-agent/org-agent/team/${teamId}`,
  seoAgentOrgTeamComplete: (teamId: string) =>
    `/api/v1/seo-agent/org-agent/team/${teamId}/complete`,
  seoAgentOrgTeamExecute: (teamId: string) => `/api/v1/seo-agent/org-agent/team/${teamId}/execute`,
  seoAgentOrgTeamLive: (teamId: string) => `/api/v1/seo-agent/org-agent/team/${teamId}/live`,
  seoAgentOrgTeamExecutions: (teamId: string) =>
    `/api/v1/seo-agent/org-agent/team/${teamId}/executions`,
  seoAgentOrgExecution: (executionId: string) =>
    `/api/v1/seo-agent/org-agent/executions/${executionId}`,

  /** Running the SEO plan, task by task. */
  seoAgentPlanNext: '/api/v1/seo-agent/execute-plan/next',
  seoAgentPlanNextLive: '/api/v1/seo-agent/execute-plan/next/live',
  seoAgentPlanTaskRun: (taskId: string) => `/api/v1/seo-agent/execute-plan/task/${taskId}/run`,
  seoAgentPlanTaskRunLive: (taskId: string) =>
    `/api/v1/seo-agent/execute-plan/task/${taskId}/run/live`,
  seoAgentPlanTaskApprove: (taskId: string) =>
    `/api/v1/seo-agent/execute-plan/task/${taskId}/approve`,
  seoAgentPlanTaskDeny: (taskId: string) => `/api/v1/seo-agent/execute-plan/task/${taskId}/deny`,
  seoAgentPlanTaskRegenerate: (taskId: string) =>
    `/api/v1/seo-agent/execute-plan/task/${taskId}/regenerate`,
  seoAgentPlanTaskRetry: (taskId: string) => `/api/v1/seo-agent/execute-plan/task/${taskId}/retry`,
  seoAgentPlanTaskPageChanges: (taskId: string) =>
    `/api/v1/seo-agent/execute-plan/task/${taskId}/page-changes`,
  seoAgentPlanPause: '/api/v1/seo-agent/execute-plan/pause',
  seoAgentPlanResume: '/api/v1/seo-agent/execute-plan/resume',
  seoAgentPlanStatus: '/api/v1/seo-agent/execute-plan/status',
  seoAgentPlanHistoryDay: '/api/v1/seo-agent/execute-plan/history/day',
  seoAgentPlanHistoryTask: (taskId: string) =>
    `/api/v1/seo-agent/execute-plan/history/task/${taskId}`,
  seoAgentPlanHistorySummary: '/api/v1/seo-agent/execute-plan/history/summary',
  seoAgentPlanActivity: '/api/v1/seo-agent/execute-plan/activity',
  seoAgentPlanActivityRun: (runId: string) => `/api/v1/seo-agent/execute-plan/activity/${runId}`,
  seoAgentPlanRegenerate: '/api/v1/seo-agent/execute-plan/regenerate',
  seoAgentPlanRegenerateStatus: '/api/v1/seo-agent/execute-plan/regenerate/status',
  seoAgentPlanHistoryPlans: '/api/v1/seo-agent/execute-plan/history-plans',
  seoAgentPlanHistoryPlan: (planId: string) =>
    `/api/v1/seo-agent/execute-plan/history-plans/${planId}`,
  seoAgentPlanChangeNotes: '/api/v1/seo-agent/execute-plan/change-notes',

  /**
   * Create page or post. GET lists the requests; `Live` is POST to start one (streams
   * `text/event-stream`) and GET to attach to the one already running; a request is addressed by
   * its public identifier.
   */
  seoAgentCustomContent: '/api/v1/seo-agent/custom-content',
  seoAgentCustomContentLive: '/api/v1/seo-agent/custom-content/live',
  seoAgentCustomContentRun: (runId: string) => `/api/v1/seo-agent/custom-content/${runId}`,
  seoAgentCustomContentApprove: (runId: string) =>
    `/api/v1/seo-agent/custom-content/${runId}/approve`,
  seoAgentCustomContentDeny: (runId: string) => `/api/v1/seo-agent/custom-content/${runId}/deny`,

  /** Search Console analytics for the caller's site. */
  seoAgentGscOverview: '/api/v1/seo-agent/analytics/gsc/overview',
  seoAgentGscTopPages: '/api/v1/seo-agent/analytics/gsc/top-pages',
  seoAgentGscKeywordGaps: '/api/v1/seo-agent/analytics/gsc/keyword-gaps',
  seoAgentGscMyKeywords: '/api/v1/seo-agent/analytics/gsc/my-keywords',
  seoAgentGscKeywordHistory: '/api/v1/seo-agent/analytics/gsc/keyword-history',
  seoAgentGscPageDetail: '/api/v1/seo-agent/analytics/gsc/page-detail',
  seoAgentGscTrend: '/api/v1/seo-agent/analytics/gsc/trend',
  seoAgentGscDailyTrend: '/api/v1/seo-agent/analytics/gsc/daily-trend',
  seoAgentGscContentGaps: '/api/v1/seo-agent/analytics/gsc/content-gaps',
  seoAgentNearWins: '/api/v1/seo-agent/analytics/near-wins',
  seoAgentWeeklyReport: '/api/v1/seo-agent/analytics/weekly-report',

  /** Competitor keyword research. */
  seoAgentCompetitorKeywords: '/api/v1/seo-agent/analytics/competitors/keywords',
  seoAgentCompetitorAnalyze: '/api/v1/seo-agent/analytics/competitors/analyze',
  seoAgentCompetitorHistory: '/api/v1/seo-agent/analytics/competitors/history',
  seoAgentCompetitorOpportunities: '/api/v1/seo-agent/analytics/competitors/opportunities',
  seoAgentCompetitorAddToPlan: '/api/v1/seo-agent/analytics/competitors/keywords/add-to-plan',

  /** The agent activity panel. */
  seoAgentActivity: '/api/v1/seo-agent/agent-activity',
  /**
   * The company's connected LinkedIn accounts.
   *
   * <p>`GET` lists every one the acting company has connected, newest first, **including
   * disconnected ones** (`loginStatus` `revoked`) — backend `linkedin-ai-agent.md` LA-10. Not
   * paged, so there is no envelope to unwrap. `POST` connects one: without cookies it is
   * `pending` until a login through the browser agent finishes (LA-4), with cookies it is
   * `active` at once (LA-5).
   */
  linkedinAgentCredentials: '/api/v1/linkedin-agent/credentials',

  /** The accounts the agent may use: `isActive` true only (LA-11). */
  linkedinAgentActiveCredentials: '/api/v1/linkedin-agent/credentials/active',

  /**
   * One account, by its public identifier.
   *
   * <p>`PATCH` replaces its cookies and reactivates it (LA-14); `DELETE` disconnects it — marks
   * it `revoked` and inactive together, deletes nothing (LA-16). An identifier that is unknown,
   * malformed or another company's answers the same 404 (LA-17).
   */
  linkedinAgentCredential: (externalId: string) =>
    `/api/v1/linkedin-agent/credentials/${externalId}`,

  /** Opens the browser agent's login window. `POST`, answers 202 (LA-18). */
  linkedinAgentCredentialSetup: (externalId: string) =>
    `/api/v1/linkedin-agent/credentials/${externalId}/setup`,

  /** How the login is going — what the screen polls. `GET` (LA-27). */
  linkedinAgentCredentialStatus: (externalId: string) =>
    `/api/v1/linkedin-agent/credentials/${externalId}/status`,

  /** Switches the account to a different LinkedIn login. `POST`, answers 202 (LA-22). */
  linkedinAgentCredentialReconnect: (externalId: string) =>
    `/api/v1/linkedin-agent/credentials/${externalId}/reconnect`,

  /** Cancels a login the user abandoned. `POST` (LA-25). */
  linkedinAgentCredentialCancel: (externalId: string, sessionId: string) =>
    `/api/v1/linkedin-agent/credentials/${externalId}/sessions/${encodeURIComponent(sessionId)}/cancel`,

  /** Pauses automation for one account. `POST` (LA-30). */
  linkedinAgentCredentialPause: (externalId: string) =>
    `/api/v1/linkedin-agent/credentials/${externalId}/pause`,

  /** Resumes automation for one account. `POST` (LA-33). */
  linkedinAgentCredentialResume: (externalId: string) =>
    `/api/v1/linkedin-agent/credentials/${externalId}/resume`,

  // The setup wizard's configuration and its "Generate with AI" helpers (LA-77..LA-98).
  /** The company's configuration. `GET` reads (404 = nothing saved yet), `PUT` saves in full. */
  linkedinAgentConfiguration: '/api/v1/linkedin-agent/configuration',
  /** `PATCH` autosaves a draft, with no validation. */
  linkedinAgentConfigurationDraft: '/api/v1/linkedin-agent/configuration/draft',
  /** `PATCH` edits a launched configuration; a note is required. */
  linkedinAgentConfigurationEdit: '/api/v1/linkedin-agent/configuration/edit',
  /** `GET` every earlier version, newest first. */
  linkedinAgentConfigurationHistory: '/api/v1/linkedin-agent/configuration/history',
  /** `GET` the newest earlier version, 404 when there is none. */
  linkedinAgentConfigurationHistoryLatest: '/api/v1/linkedin-agent/configuration/history/latest',
  /** `PATCH` picks or clears the entry mode. */
  linkedinAgentConfigurationMode: '/api/v1/linkedin-agent/configuration/mode',
  /** `PATCH` records that the AI suggestion step ran. */
  linkedinAgentConfigurationSuggestionsGenerated:
    '/api/v1/linkedin-agent/configuration/suggestions-generated',
  /** `GET` the companies already viewed, for the "Generate with AI" picker. */
  linkedinAgentConfigurationCompanies: '/api/v1/linkedin-agent/configuration/companies',
  /** `POST` suggested values for the four steps from one viewed company. */
  linkedinAgentConfigurationSuggest: '/api/v1/linkedin-agent/configuration/suggest',

  /** `POST` a live company-page view; `GET ?companyUrl=` reads the stored one (LA-66, LA-67). */
  linkedinAgentCompanyPageView: '/api/v1/linkedin-agent/company-pages/view',
  // The company's AI team: built from the configuration by the organization agent (LA-99..LA-114).
  /** `POST` builds the team (or returns the existing one), `GET` reads it (404 = not built yet). */
  linkedinAgentTeam: '/api/v1/linkedin-agent/organization',
  /** `POST` builds the team again from the latest configuration, with a new strategy prompt. */
  linkedinAgentTeamRegenerate: '/api/v1/linkedin-agent/organization/regenerate',
  /** `PUT` renames the leader and members. */
  linkedinAgentTeamById: (teamId: string) => `/api/v1/linkedin-agent/organization/${teamId}`,
  /** `PUT` replaces the strategy prompt, with the reason. */
  linkedinAgentTeamStrategy: (teamId: string) =>
    `/api/v1/linkedin-agent/organization/${teamId}/strategy`,
  // The reference list, the targeting brief written from it and the plan built from the brief
  // (LA-115..LA-133).
  /** `GET ?type=` lists the references, `POST` adds one. */
  linkedinAgentReferences: '/api/v1/linkedin-agent/reference-profiles',
  /** `POST` adds the rows of a CSV file. */
  linkedinAgentReferencesImport: '/api/v1/linkedin-agent/reference-profiles/import',
  /** `DELETE` removes one reference. */
  linkedinAgentReferenceById: (referenceId: string) =>
    `/api/v1/linkedin-agent/reference-profiles/${referenceId}`,
  /** `POST` puts one reference back in the queue. */
  linkedinAgentReferenceRetry: (referenceId: string) =>
    `/api/v1/linkedin-agent/reference-profiles/${referenceId}/retry`,
  /** `POST` starts the lookup of one reference in the background (202). */
  linkedinAgentReferenceVerifyNow: (referenceId: string) =>
    `/api/v1/linkedin-agent/reference-profiles/${referenceId}/verify-now`,
  /** `POST ?type=` starts the lookup of every waiting reference of a type (202). */
  linkedinAgentReferencesVerifyAll: '/api/v1/linkedin-agent/reference-profiles/verify-all',
  /** `POST` approves the prospects made from verified people. */
  linkedinAgentReferencesApproveProspects:
    '/api/v1/linkedin-agent/reference-profiles/approve-prospects',
  /** `GET` the newest analysis (204 = none yet). */
  linkedinAgentReferenceAnalysis: '/api/v1/linkedin-agent/reference-profiles/analysis',
  /** `POST` has the model write a new analysis. */
  linkedinAgentReferenceAnalysisGenerate:
    '/api/v1/linkedin-agent/reference-profiles/analysis/generate',
  /** `PUT` saves an edit of the analysis. */
  linkedinAgentReferenceAnalysisById: (analysisId: string) =>
    `/api/v1/linkedin-agent/reference-profiles/analysis/${analysisId}`,
  /** `POST` approves an analysis. */
  linkedinAgentReferenceAnalysisApprove: (analysisId: string) =>
    `/api/v1/linkedin-agent/reference-profiles/analysis/${analysisId}/approve`,
  /** `GET` the live plan and the configuration search switch. */
  linkedinAgentReferencePlan: '/api/v1/linkedin-agent/reference-profiles/plan',
  /** `POST` generates a plan from the approved analysis. */
  linkedinAgentReferencePlanGenerate: '/api/v1/linkedin-agent/reference-profiles/plan/generate',
  /** `PUT` pauses or resumes the plan. */
  linkedinAgentReferencePlanPaused: '/api/v1/linkedin-agent/reference-profiles/plan/paused',
  /** `GET` the live plan's days, for the strategy calendar (204 = none yet). */
  linkedinAgentReferencePlanDays: '/api/v1/linkedin-agent/reference-profiles/plan/days',
  /** `PUT` pauses or resumes the configuration's people search. */
  linkedinAgentReferenceConfigSearchPaused:
    '/api/v1/linkedin-agent/reference-profiles/plan/config-search-paused',
  // The strategy: the plan the agent writes for the account, and what a person does to it (LA-193..LA-221).
  /** `GET` the plan as one calendar (404 = never generated). */
  linkedinAgentStrategy: '/api/v1/linkedin-agent/strategy',
  /** `POST` starts a generation (202). */
  linkedinAgentStrategyGenerate: '/api/v1/linkedin-agent/strategy/generate',
  /** `POST` replans only the days that are left (202). */
  linkedinAgentStrategyRegenerate: '/api/v1/linkedin-agent/strategy/regenerate',
  linkedinAgentStrategyRenew: '/api/v1/linkedin-agent/strategy/renew',
  /** `PUT` writes the day's requirement. */
  linkedinAgentStrategyDayNote: (date: string) =>
    `/api/v1/linkedin-agent/strategy/day/${date}/note`,
  /** `PUT` edits one action of a day. */
  linkedinAgentStrategyAction: (date: string, actionId: string) =>
    `/api/v1/linkedin-agent/strategy/day/${date}/action/${encodeURIComponent(actionId)}`,
  /** `POST` rejects one action, or replaces it. */
  linkedinAgentStrategyActionReject: (date: string, actionId: string) =>
    `/api/v1/linkedin-agent/strategy/day/${date}/action/${encodeURIComponent(actionId)}/reject`,
  /** `POST` has the model rewrite one action. */
  linkedinAgentStrategyActionRegenerate: (date: string, actionId: string) =>
    `/api/v1/linkedin-agent/strategy/day/${date}/action/${encodeURIComponent(actionId)}/regenerate`,
  /** `POST` runs a failed search again. */
  linkedinAgentStrategyActionRetry: (date: string, actionId: string) =>
    `/api/v1/linkedin-agent/strategy/day/${date}/action/${encodeURIComponent(actionId)}/retry`,
  // The daily prospect batch: which prospect gets which action today (LA-222..LA-235).
  /** `GET` a day batch (`?date=`, today when left out). */
  linkedinAgentStrategyDaily: '/api/v1/linkedin-agent/strategy/daily',
  /** `GET` one status per decided day (`?start=&end=`). */
  linkedinAgentStrategyDailyRange: '/api/v1/linkedin-agent/strategy/daily/range',
  /** `GET` one prospect row for every decided day (`?start=&end=`). */
  linkedinAgentStrategyDailyProspect: (prospectId: string) =>
    `/api/v1/linkedin-agent/strategy/daily/prospect/${prospectId}`,
  /** `POST` asks the agent to decide today action for one prospect. */
  linkedinAgentStrategyDailyDecideOne: (prospectId: string) =>
    `/api/v1/linkedin-agent/strategy/daily/decide-one/${prospectId}`,
  /** `POST` rejects today whole batch. */
  linkedinAgentStrategyDailyReject: '/api/v1/linkedin-agent/strategy/daily/reject',
  /** `PUT` edits one action of today batch. */
  linkedinAgentStrategyDailyAction: (index: number) =>
    `/api/v1/linkedin-agent/strategy/daily/action/${String(index)}`,
  /** `POST` rejects one action, or replaces it. */
  linkedinAgentStrategyDailyActionReject: (index: number) =>
    `/api/v1/linkedin-agent/strategy/daily/action/${String(index)}/reject`,
  /** `POST` has the model rewrite one action. */
  linkedinAgentStrategyDailyActionRegenerate: (index: number) =>
    `/api/v1/linkedin-agent/strategy/daily/action/${String(index)}/regenerate`,
  // The conversations: prospect threads, the drafts waiting for a person, and comments on prospect posts (LA-236..LA-259).
  /** `GET` one entry per prospect with their thread and the draft waiting for review. */
  linkedinAgentConversations: '/api/v1/linkedin-agent/conversations',
  /** `GET` the comments on prospect posts. */
  linkedinAgentPostComments: '/api/v1/linkedin-agent/conversations/post-comments',
  /** `POST` approves the reply waiting for a prospect. */
  linkedinAgentReplyApprove: (prospectId: string) =>
    `/api/v1/linkedin-agent/conversations/${prospectId}/reply/approve`,
  /** `POST` rejects it. */
  linkedinAgentReplyReject: (prospectId: string) =>
    `/api/v1/linkedin-agent/conversations/${prospectId}/reply/reject`,
  /** `PUT` replaces its text. */
  linkedinAgentReplyEdit: (prospectId: string) =>
    `/api/v1/linkedin-agent/conversations/${prospectId}/reply`,
  /** `POST` approves a comment draft. */
  linkedinAgentCommentApprove: (draftId: string) =>
    `/api/v1/linkedin-agent/conversations/post-comments/${draftId}/approve`,
  /** `POST` rejects it. */
  linkedinAgentCommentReject: (draftId: string) =>
    `/api/v1/linkedin-agent/conversations/post-comments/${draftId}/reject`,
  /** `PUT` replaces its text. */
  linkedinAgentCommentEdit: (draftId: string) =>
    `/api/v1/linkedin-agent/conversations/post-comments/${draftId}`,
  // The company's LinkedIn posts: drafting, reviewing, publishing (LA-260..LA-287).
  /** `GET` the company's posts, newest first. */
  linkedinAgentPosts: '/api/v1/linkedin-agent/posts',
  /** `POST` has the agent write a post. */
  linkedinAgentPostsGenerate: '/api/v1/linkedin-agent/posts/generate',
  /** `POST` a post a person wrote. */
  linkedinAgentPostsManual: '/api/v1/linkedin-agent/posts/manual',
  /** `POST` a stock photo for text being written. */
  linkedinAgentPostsSuggestImage: '/api/v1/linkedin-agent/posts/suggest-image',
  /** `GET` one post, `PUT` replaces its text. */
  linkedinAgentPost: (postId: string) => `/api/v1/linkedin-agent/posts/${postId}`,
  /** `POST` approves a post and queues it for publishing. */
  linkedinAgentPostApprove: (postId: string) => `/api/v1/linkedin-agent/posts/${postId}/approve`,
  /** `POST` rejects a post, with reasons. */
  linkedinAgentPostReject: (postId: string) => `/api/v1/linkedin-agent/posts/${postId}/reject`,
  /** `GET` the stored picture, `PUT` uploads one, `DELETE` removes it. */
  linkedinAgentPostImage: (postId: string) => `/api/v1/linkedin-agent/posts/${postId}/image`,
  /** `POST` picks a new stock photo for the post. */
  linkedinAgentPostImageSuggest: (postId: string) =>
    `/api/v1/linkedin-agent/posts/${postId}/image/suggest`,
  // The activity feed, its overview, whether the agent is working, and the analytics (LA-288..LA-312).
  /** `GET` a page of the activity feed (cursor paging), optionally for one prospect. */
  linkedinAgentActivity: '/api/v1/linkedin-agent/activity',
  /** `GET` the overview cards and the category breakdown. */
  linkedinAgentActivityStats: '/api/v1/linkedin-agent/activity/stats',
  /** `GET` whether the agent is working on the account right now. */
  linkedinAgentStatus: '/api/v1/linkedin-agent/activity/agent-status',
  linkedinAgentWorkToday: '/api/v1/linkedin-agent/activity/work-today',
  /** `GET` the company's analytics for a range ending on a date. */
  linkedinAgentAnalytics: '/api/v1/linkedin-agent/analytics',
  /** `GET` one prospect's last seven days. */
  linkedinAgentProspectAnalytics: (prospectId: string) =>
    `/api/v1/linkedin-agent/analytics/prospects/${prospectId}`,
  // The company's prospects: the pipeline the agent works (LA-143..LA-161).
  /** `GET` a page of prospects (cursor paging), `POST` adds one by hand. */
  linkedinAgentProspects: '/api/v1/linkedin-agent/prospects',
  /** `GET` the tab counts: ALL, each stage, GIVEN_UP. */
  linkedinAgentProspectStats: '/api/v1/linkedin-agent/prospects/stats',
  /** `GET` what the job title and source dropdowns offer. */
  linkedinAgentProspectFilterOptions: '/api/v1/linkedin-agent/prospects/filter-options',
  /** `POST` searches LinkedIn for people (a name, or the saved configuration). */
  linkedinAgentProspectSearch: '/api/v1/linkedin-agent/prospects/search',
  /** `POST` adds the one person behind a LinkedIn address, already approved. */
  linkedinAgentProspectSearchByUrl: '/api/v1/linkedin-agent/prospects/search-by-url',
  /** `GET` one prospect, with its score's reasons and stage history. */
  linkedinAgentProspectById: (prospectId: string) =>
    `/api/v1/linkedin-agent/prospects/${prospectId}`,
  /** `POST` approves a prospect. */
  linkedinAgentProspectApprove: (prospectId: string) =>
    `/api/v1/linkedin-agent/prospects/${prospectId}/approve`,
  /** `POST` rejects a prospect. */
  linkedinAgentProspectReject: (prospectId: string) =>
    `/api/v1/linkedin-agent/prospects/${prospectId}/reject`,
  /** `POST` stops all further automation for a prospect. */
  linkedinAgentProspectGiveUp: (prospectId: string) =>
    `/api/v1/linkedin-agent/prospects/${prospectId}/give-up`,
  /** `PUT` moves a prospect to another stage. */
  linkedinAgentProspectStage: (prospectId: string) =>
    `/api/v1/linkedin-agent/prospects/${prospectId}/stage`,

  // The Support area: the Help Center's questions and the likes and dislikes on them.
  /** `GET` every question, newest first (`?category=` for one category). */
  supportFaqs: '/api/v1/support/faqs',
  /** `POST` adds a question. Super admin. */
  supportFaqAdd: '/api/v1/support/faqs/add',
  /** `PUT` replaces one question and `DELETE` removes it. Super admin. */
  supportFaq: (faqId: string) => `/api/v1/support/faqs/${faqId}`,
  /** `GET` where one thing stands on likes and dislikes, and what the caller said about it. */
  reaction: (targetType: string, targetId: string) => `/api/v1/reactions/${targetType}/${targetId}`,
  /** `POST` likes it, or takes the like back. */
  reactionLike: (targetType: string, targetId: string) =>
    `/api/v1/reactions/${targetType}/${targetId}/like`,
  /** `POST` dislikes it, or takes the dislike back. */
  reactionDislike: (targetType: string, targetId: string) =>
    `/api/v1/reactions/${targetType}/${targetId}/dislike`,
  /** `POST` takes back whichever reaction the caller had. */
  reactionClear: (targetType: string, targetId: string) =>
    `/api/v1/reactions/${targetType}/${targetId}/clear`,

  // Documentation pages.
  /** `GET` the published pages, with their likes and dislikes (`?page=&size=`). */
  supportDocsPublished: '/api/v1/support/documentation/published/all',
  /** `GET` the pages whose title or description holds `?query=`, whatever their status. */
  supportDocsSearch: '/api/v1/support/documentation/search',
  /** `GET` one page. */
  supportDoc: (documentId: string) => `/api/v1/support/documentation/${documentId}`,
  /** `GET` every page whatever its status (`?page=&size=`). Super admin. */
  supportDocsAll: '/api/v1/support/documentation',
  /** `POST` adds a page written in the editor. Super admin. */
  supportDocsHtml: '/api/v1/support/documentation/html-content',
  /** `POST` adds a step-by-step guide. Super admin. */
  supportDocsGuide: '/api/v1/support/documentation/guide-steps',
  /** `POST` (multipart) adds a page made of an uploaded file. Super admin. */
  supportDocsUpload: '/api/v1/support/documentation/upload',
  /** `PUT` (multipart) changes an uploaded page, and replaces its file when one is sent. Super admin. */
  supportDocUpdateFile: (documentId: string) =>
    `/api/v1/support/documentation/file/update/${documentId}`,
  /** `PUT` changes an editor page or a guide. `DELETE` removes any page. Super admin. */
  supportDocWrite: (documentId: string) => `/api/v1/support/documentation/${documentId}`,
  /** `PATCH` sets a page's status with `?status=`. Super admin. */
  supportDocStatus: (documentId: string) => `/api/v1/support/documentation/${documentId}/status`,
  /** `GET` the file of an uploaded page, as bytes. Needs the bearer token. */
  supportDocFile: (documentId: string) => `/api/v1/support/documentation/${documentId}/file`,

  // The Support Center: the caller's own tickets.
  /** `GET` the caller's tickets, newest first. `POST` (multipart) opens one, with an optional attachment. */
  supportTickets: '/api/v1/support/tickets',
  /** `GET` one ticket and `PATCH` its subject or description. */
  supportTicket: (ticketId: string) => `/api/v1/support/tickets/${ticketId}`,
  /** `GET` the replies on a ticket, oldest first. `POST` (multipart) adds one, with an optional attachment. */
  supportTicketMessages: (ticketId: string) => `/api/v1/support/tickets/${ticketId}/messages`,
  /** `GET` what happened to a ticket, newest first. */
  supportTicketActivities: (ticketId: string) => `/api/v1/support/tickets/${ticketId}/activities`,
  /** `GET` a file on a ticket, as bytes. Needs the bearer token. */
  supportTicketAttachment: (ticketId: string, attachmentId: string) =>
    `/api/v1/support/tickets/${ticketId}/attachments/${attachmentId}`,
  // Ticket management: the platform operator's view of every ticket. Super admin.
  /** `GET` every ticket, newest first, with who raised it. */
  supportAdminTickets: '/api/v1/support/admin/tickets',
  /** `GET` one ticket. */
  supportAdminTicket: (ticketId: string) => `/api/v1/support/admin/tickets/${ticketId}`,
  /** `GET` the replies on a ticket, oldest first. */
  supportAdminTicketMessages: (ticketId: string) =>
    `/api/v1/support/admin/tickets/${ticketId}/messages`,
  /** `GET` a file on a ticket, as bytes. Needs the bearer token. */
  supportAdminTicketAttachment: (ticketId: string, attachmentId: string) =>
    `/api/v1/support/admin/tickets/${ticketId}/attachments/${attachmentId}`,
  /** `POST` (multipart) replies as support, with an optional attachment. */
  supportAdminTicketReply: (ticketId: string) => `/api/v1/support/admin/tickets/${ticketId}/reply`,
  /** `PUT` marks the ticket resolved. */
  supportAdminTicketResolve: (ticketId: string) =>
    `/api/v1/support/admin/tickets/${ticketId}/resolve`,
  /** `PUT` closes the ticket. */
  supportAdminTicketClose: (ticketId: string) => `/api/v1/support/admin/tickets/${ticketId}/close`,
  /** `PUT` changes the status and the priority, either or both. */
  supportAdminTicketUpdate: (ticketId: string) =>
    `/api/v1/support/admin/tickets/${ticketId}/update`,
  /** `PUT` assigns the ticket to one of the people `supportAdminAssignable` lists. */
  supportAdminTicketAssign: (ticketId: string) =>
    `/api/v1/support/admin/tickets/${ticketId}/assign`,
  /** `GET` the people a ticket can be assigned to. */
  supportAdminAssignable: '/api/v1/support/admin/users/assignable',
  /** `PUT` marks the ticket resolved. */
  supportTicketResolve: (ticketId: string) => `/api/v1/support/tickets/${ticketId}/resolve`,
  /** `PUT` closes the ticket. */
  supportTicketClose: (ticketId: string) => `/api/v1/support/tickets/${ticketId}/close`,
  /** `PUT` sets the ticket back to open. */
  supportTicketReopen: (ticketId: string) => `/api/v1/support/tickets/${ticketId}/reopen`,

  /**
   * Starts connecting the caller's own Slack account. GET, answers Slack's install address, which the
   * browser opens in a window; the window reports back with `XTREMAND_SLACK_OAUTH`.
   */
  slackAuthorize: '/api/v1/slack/authorize',
  /** `GET` whether the caller has connected Slack; `DELETE` disconnects. */
  slackConnection: '/api/v1/slack/connection',
  /** `POST` sends the caller a test message in Slack. */
  slackConnectionTest: '/api/v1/slack/connection/test',
  /** `GET` one agent's Slack events (`?category=`); `PUT` turns on exactly the ones named. */
  slackNotificationPreferences: '/api/v1/slack/notification-preferences',
  /** `GET` how the caller's agent notifications reach them; `PUT` chooses SLACK, EMAIL or BOTH. */
  notificationChannel: '/api/v1/notifications/channel-preference',
  /** `GET` the caller's notifications, newest first, by cursor. */
  alerts: '/api/v1/alerts',
  /** `GET` total, unread, starred and the last seven days. `unread` is what the bell shows. */
  alertSummary: '/api/v1/alerts/summary',
  /** `POST` marks every notification read. */
  alertsReadAll: '/api/v1/alerts/read-all',
  /** `POST` removes every read notification that is not starred. */
  alertsClearRead: '/api/v1/alerts/clear-read',
  /** `DELETE` removes one notification. */
  alert: (externalId: string) => `/api/v1/alerts/${externalId}`,
  /** `POST` marks one notification read. */
  alertRead: (externalId: string) => `/api/v1/alerts/${externalId}/read`,
  /** `PUT` stars one notification; `DELETE` unstars it. */
  alertStar: (externalId: string) => `/api/v1/alerts/${externalId}/star`,
} as const;
